import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { selectIdentity } from "../../../selectors/userSelectors";
import Field from "../../../shared/ui/Field/Field";
import Select from "../../../shared/ui/Field/Select";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
  memberDisplayName,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import {
  buildLocationApiPayload,
  validateLocationCanonical,
  validateLocationCoordinates,
  validateLocationStructured,
} from "../../geo/lib/locationGeo";
import LandParcelsEditor from "../../landParcels/widgets/LandParcelsEditor";
import CreateStepper from "../../createRealty/widgets/CreateStepper";
import CreateNav from "../../createRealty/widgets/CreateNav";
import CreateStepType from "../../createRealty/widgets/CreateStepType";
import CreateStepLocation from "../../createRealty/widgets/CreateStepLocation";
import CreateStepSpecs from "../../createRealty/widgets/CreateStepSpecs";
import CreateStepDescription from "../../createRealty/widgets/CreateStepDescription";
import EditSectionMedia from "../../editRealty/widgets/EditSectionMedia";
import {
  SUBTYPE_BY_PROPERTY_TYPE,
  buildCommercialPayload,
  buildDraftBootstrapPayload,
  buildInitialForm,
  buildLandPayload,
  buildResidentialPayload,
  coerceNullable,
  collectSpecsFieldIssues,
  formFromPropertyDetail,
  validateStep1ForDraft,
} from "../../createRealty/lib/wizardContract";

import { createCompanyPropertyTransport } from "../api/companyProperties";
import { isCorporatePropertiesAvailable } from "../lib/propertyLabels";
import {
  createAuthorityBoundLandParcelsApi,
  createAuthorityBoundMediaApi,
  membershipGenerationOf,
} from "../lib/authorityBoundTransport";
import {
  buildDraftBinding,
  draftBindingMatches as draftBindingMatchesAuthority,
  resolveRecoverableDraftId,
} from "../lib/draftBinding";

const COMPANY_D1_FIELD_ERROR_KEYS = [
  "title",
  "description_public",
  "description_private",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "video_url",
  "canonical_region",
  "canonical_settlement",
  "local_area",
  "address_object_id",
  "manual_address_object",
  "building_number",
  "legacy_address",
  "residential_complex",
  "total_area_m2",
  "living_area_m2",
  "kitchen_area_m2",
  "rooms",
  "floor",
  "total_floors",
  "land_area_m2",
  "area_m2",
  "latitude",
  "longitude",
  "cooperation_terms",
];

const dismissFieldErrorsForKeys = (setFieldErrors, keys) => {
  if (!keys || !keys.length) return;
  setFieldErrors((prev) => {
    let changed = false;
    const next = { ...prev };
    for (const key of keys) {
      if (next[key] !== undefined) {
        delete next[key];
        changed = true;
      }
    }
    return changed ? next : prev;
  });
};

const changedKeys = (prevObj, nextObj) => {
  const keys = new Set([
    ...Object.keys(prevObj || {}),
    ...Object.keys(nextObj || {}),
  ]);
  const out = [];
  for (const key of keys) {
    if ((prevObj || {})[key] !== (nextObj || {})[key]) out.push(key);
  }
  return out;
};

const COMPANY_STEPS = [
  { n: 1, label: "Тип і базове", originalN: 1 },
  { n: 2, label: "Локація", originalN: 2 },
  { n: 3, label: "Характеристики", originalN: 3 },
  { n: 4, label: "Фото", originalN: 5 },
  { n: 5, label: "Опис", originalN: 6 },
];

function buildCorporateCorePayload(core) {
  return {
    title: core.title,
    description_public: core.description_public || "",
    description_private: core.description_private || "",
    video_url: core.video_url || "",
    property_type: core.property_type,
    deal_type: core.deal_type,
    price: core.price,
    currency: core.currency,
    has_recovery_program: Boolean(core.has_recovery_program),
    has_housing_program: Boolean(core.has_housing_program),
  };
}

function CompanyPropertyCreateContent() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const draftBindingRef = useRef(null);
  const membershipGenerationRef = useRef(null);
  const stateRef = useRef("loading");
  const formDirtyRef = useRef(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const transport = useMemo(
    () => createCompanyPropertyTransport(companyId),
    [companyId],
  );

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [state, setState] = useState("loading");
  const [writesSuspended, setWritesSuspended] = useState(false);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;

  const markFormDirty = () => {
    formDirtyRef.current = true;
  };
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => buildInitialForm());
  const [propertyId, setPropertyId] = useState(null);
  const [property, setProperty] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [landParcels, setLandParcels] = useState([]);
  const [childSessionKey, setChildSessionKey] = useState(0);
  const [formLocked, setFormLocked] = useState(false);
  const [responsibilityMode, setResponsibilityMode] = useState("self");
  const [responsibilityUserId, setResponsibilityUserId] = useState("");
  const [rosterMembers, setRosterMembers] = useState([]);

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const available =
    authorityMatches && isCorporatePropertiesAvailable(company);
  const canManage =
    authorityMatches && isAdminMembership(company, membership);
  const canCreate =
    available && isActiveMembership(company, membership);

  const bumpChildSession = useCallback(() => {
    setChildSessionKey((value) => value + 1);
  }, []);

  const clearDraftBinding = useCallback(() => {
    draftBindingRef.current = null;
  }, []);

  const authoritySnapshot = useCallback(
    (resourceId = null) => ({
      companyId,
      actorId,
      membershipGeneration: membershipGenerationRef.current,
      resourceId,
    }),
    [actorId, companyId],
  );

  const liveDraftAuthority = useCallback(
    () => ({
      companyId,
      actorId,
      membershipGeneration: membershipGenerationRef.current,
    }),
    [actorId, companyId],
  );

  const draftBindingMatches = useCallback(
    (binding) => draftBindingMatchesAuthority(binding, liveDraftAuthority()),
    [liveDraftAuthority],
  );

  const rememberDraft = useCallback(
    (id) => {
      draftBindingRef.current = buildDraftBinding({
        propertyId: id,
        companyId,
        actorId,
        membershipGeneration: membershipGenerationRef.current,
      });
      setPropertyId(id);
    },
    [actorId, companyId],
  );

  const clearMutationUi = useCallback(
    ({ keepDraftBinding = false } = {}) => {
      setForm(buildInitialForm());
      setProperty(null);
      setFieldErrors({});
      setActionError(null);
      setLandParcels([]);
      setStep(1);
      setResponsibilityMode("self");
      setResponsibilityUserId("");
      bumpChildSession();
      if (keepDraftBinding && draftBindingMatches(draftBindingRef.current)) {
        setPropertyId(draftBindingRef.current.id);
      } else {
        clearDraftBinding();
        setPropertyId(null);
      }
    },
    [bumpChildSession, clearDraftBinding, draftBindingMatches],
  );

  const handleAuthorityLoss = useCallback(
    (status) => {
      busyOpRef.current = null;
      setBusy(false);
      clearDraftBinding();
      clearMutationUi();
      setFormLocked(true);
      setState(status === 404 ? "not-found" : "denied");
    },
    [clearDraftBinding, clearMutationUi],
  );

  const bindLiveAuthority = useCallback(
    (nextMembership, resourceId = null) => {
      const generation = membershipGenerationOf(nextMembership);
      membershipGenerationRef.current = generation;
      scopeGate.bindIdentity({
        companyId,
        actorId,
        membershipGeneration: generation,
        resourceId,
      });
    },
    [actorId, companyId, scopeGate],
  );

  const loadAuthority = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return null;
    }
    const keepVisible = background && stateRef.current === "ready";
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
      setFormLocked(true);
    }
    const op = scopeGate.beginRead({
      companyId,
      actorId,
      membershipGeneration: membershipGenerationRef.current,
    });
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return null;
      }
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      bindLiveAuthority(mineMatches, draftBindingRef.current?.id || null);
      const synced = scopeGate.beginRead({
        companyId,
        actorId,
        membershipGeneration: membershipGenerationOf(mineMatches),
        resourceId: draftBindingRef.current?.id || null,
      });
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);
      if (!mineCompanyMatches || !isActiveMembership(companyData, mineMatches)) {
        clearMutationUi();
        setRosterMembers([]);
        setState("denied");
        return null;
      }
      if (!isCorporatePropertiesAvailable(companyData)) {
        clearMutationUi();
        setRosterMembers([]);
        setState("unavailable");
        return null;
      }
      if (!scopeGate.readStillValid(synced, { mounted: mountedRef.current })) {
        return null;
      }
      if (isAdminMembership(companyData, mineMatches)) {
        try {
          const roster = await fetchMembers(companyId, "active");
          if (
            !scopeGate.readStillValid(synced, { mounted: mountedRef.current })
          ) {
            return null;
          }
          setRosterMembers(
            (Array.isArray(roster) ? roster : []).filter(
              (m) => m.status === "active",
            ),
          );
        } catch (rosterErr) {
          if (
            !scopeGate.readStillValid(synced, { mounted: mountedRef.current })
          ) {
            return null;
          }
          setRosterMembers([]);
        }
      } else {
        setRosterMembers([]);
      }
      setFormLocked(false);
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
      return { company: companyData, membership: mineMatches };
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return null;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 404 && status !== 403) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return null;
      }
      clearMutationUi();
      if (status === 404) setState("not-found");
      else if (status === 403) setState("denied");
      else {
        setError(messageFromError(err));
        setState("error");
      }
      return null;
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [
    actorId,
    bindLiveAuthority,
    clearMutationUi,
    companyId,
    scopeGate,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    membershipGenerationRef.current = null;
    clearDraftBinding();
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: null,
      resourceId: null,
    });
    busyOpRef.current = null;
    clearMutationUi();
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setRosterMembers([]);
    setResponsibilityMode("self");
    setResponsibilityUserId("");
    loadAuthority();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPropertyDetail = (detail) => {
    if (!detail || detail.id == null) {
      throw new Error("Не вдалося завантажити об'єкт");
    }
    setProperty(detail);
    setPropertyId(detail.id);
    const nextForm = formFromPropertyDetail(detail);
    setForm((prev) => ({
      ...prev,
      core: { ...prev.core, ...nextForm.core },
      location: nextForm.location || prev.location,
      residential: nextForm.residential || prev.residential,
      land: nextForm.land || prev.land,
      commercial: nextForm.commercial || prev.commercial,
    }));
  };

  const revalidateCreateFocus = async () => {
    const auth = await loadAuthority({ background: true });
    if (!mountedRef.current || !auth) return;
    const preserved = draftBindingMatches(draftBindingRef.current)
      ? { ...draftBindingRef.current }
      : null;
    if (!preserved || formDirtyRef.current) return;
    try {
      const focusReadOp = scopeGate.beginRead({
        companyId,
        actorId,
        resourceId: preserved.id,
        membershipGeneration: membershipGenerationRef.current,
      });
      const detail = await transport.fetchPropertyDetail(preserved.id);
      if (
        !scopeGate.readStillValid(focusReadOp, { mounted: mountedRef.current })
      ) {
        return;
      }
      if (!draftBindingMatches(preserved) || formDirtyRef.current) return;
      applyPropertyDetail(detail);
      bindLiveAuthority(auth.membership, detail.id);
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        handleAuthorityLoss(status);
      }
    }
  };

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy,
    onRevalidate: () => revalidateCreateFocus(),
  });

  const refreshProperty = async (id, op = null) => {
    const readOp =
      op ||
      scopeGate.beginRead({
        companyId,
        actorId,
        resourceId: id,
        membershipGeneration: membershipGenerationRef.current,
      });
    const detail = await transport.fetchPropertyDetail(id);
    if (op) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return null;
      }
    } else if (
      !scopeGate.readStillValid(readOp, { mounted: mountedRef.current })
    ) {
      return null;
    }
    if (!mountedRef.current) return null;
    applyPropertyDetail(detail);
    return detail;
  };

  const ensureDraft = async (session) => {
    let op = session.op;
    const existingId = resolveRecoverableDraftId(
      draftBindingRef.current,
      propertyId,
      liveDraftAuthority(),
    );

    // R1: strict price/title parity before create OR resumed-draft mutation.
    const issues = validateStep1ForDraft(form.core);
    if (Object.keys(issues).length) {
      setFieldErrors(issues);
      setStep(1);
      const err = new Error("validation");
      err.code = "CLIENT_VALIDATION";
      throw err;
    }

    if (existingId) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return null;
      }
      try {
        const detail = await refreshProperty(existingId, op);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return null;
        }
        if (detail) rememberDraft(detail.id);
        return detail;
      } catch (err) {
        if (scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          rememberDraft(existingId);
        }
        throw err;
      }
    }

    const body = buildDraftBootstrapPayload(form.core);
    delete body.owner;
    delete body.company;
    delete body.created_by;
    delete body.responsible_user;
    delete body.responsible_user_id;
    if (canManage) {
      if (responsibilityMode === "other") {
        // R1-1: fail closed — never omit and silently become self.
        const parsed = Number(responsibilityUserId);
        const rosterHasTarget =
          Boolean(responsibilityUserId) &&
          Number.isFinite(parsed) &&
          parsed > 0 &&
          rosterMembers.some((member) => {
            const memberId = Number(member.user?.id ?? member.user_id);
            return Number.isFinite(memberId) && memberId === parsed;
          });
        if (!rosterHasTarget) {
          setActionError(
            rosterMembers.length === 0
              ? "Немає активних учасників для призначення. Оновіть сторінку або оберіть інший режим."
              : "Оберіть учасника.",
          );
          const err = new Error("validation");
          err.code = "CLIENT_VALIDATION";
          throw err;
        }
        body.responsible_user_id = parsed;
      } else if (responsibilityMode === "unassigned") {
        body.responsible_user_id = null;
      }
      // self: omit responsible_user_id
    }
    if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
      return null;
    }
    const created = await transport.createProperty(body);
    // Invalidation before create response must not carry the returned id.
    if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
      return null;
    }
    const newId = created?.id;
    if (newId == null) {
      const err = new Error("missing_create_id");
      err.code = "MISSING_CREATE_ID";
      throw err;
    }
    // Recoverable draft binding only while the pre-create operation is live.
    rememberDraft(newId);
    bindLiveAuthority(membership, newId);
    // R2-2: successor mutation owns the new resourceId + busy flag.
    const successorOp = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: newId,
      membershipGeneration: membershipGenerationRef.current,
    });
    if (busyOpRef.current === op) {
      busyOpRef.current = successorOp;
    }
    session.op = successorOp;
    op = successorOp;
    try {
      const detail = await refreshProperty(newId, op);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return null;
      }
      return detail;
    } catch (err) {
      if (scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        rememberDraft(newId);
      }
      throw err;
    }
  };

  const saveThroughOriginalStep = async (originalN) => {
    if (!canCreate || !available || formLocked || writesSuspendedRef.current || busyOpRef.current) return false;
    const session = {
      op: scopeGate.beginMutation({
        companyId,
        actorId,
        resourceId: propertyId,
        membershipGeneration: membershipGenerationRef.current,
      }),
    };
    busyOpRef.current = session.op;
    setBusy(true);
    setActionError(null);
    setFieldErrors({});
    try {
      let current = await ensureDraft(session);
      if (!current) return false;
      const op = session.op;
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return false;
      }

      if (originalN >= 2) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return false;
        }
        const locIssues = {
          ...validateLocationCanonical(form.location),
          ...validateLocationStructured(form.location),
          ...validateLocationCoordinates(form.location),
        };
        if (Object.keys(locIssues).length) {
          setFieldErrors(locIssues);
          setStep(2);
          return false;
        }
        const locationBody = buildLocationApiPayload(
          form.location,
          coerceNullable,
        );
        if (locationBody) {
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
          await transport.putPropertyLocation(current.id, locationBody);
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
        }
      }

      if (originalN >= 3) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return false;
        }
        const specsIssues = collectSpecsFieldIssues(form);
        if (Object.keys(specsIssues).length) {
          setFieldErrors(specsIssues);
          setStep(3);
          return false;
        }
        const subtype = SUBTYPE_BY_PROPERTY_TYPE[form.core.property_type];
        if (subtype === "residential") {
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
          await transport.putPropertyResidential(
            current.id,
            buildResidentialPayload(form.residential),
          );
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
        } else if (subtype === "land") {
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
          await transport.putPropertyLand(
            current.id,
            buildLandPayload(form.land),
          );
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
        } else if (subtype === "commercial") {
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
          await transport.putPropertyCommercial(
            current.id,
            buildCommercialPayload(form.commercial),
          );
          if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
            return false;
          }
        }
      }

      if (originalN >= 6) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return false;
        }
        await transport.patchPropertyCore(
          current.id,
          buildCorporateCorePayload(form.core),
        );
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return false;
        }
      }

      await refreshProperty(current.id, op);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return false;
      }
      return true;
    } catch (err) {
      if (err?.code === "CLIENT_VALIDATION") return false;
      if (
        !scopeGate.mutationStillValid(session.op, {
          mounted: mountedRef.current,
        })
      ) {
        return false;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        handleAuthorityLoss(status);
        return false;
      }
      setFieldErrors(parseDrfFieldErrors(err, COMPANY_D1_FIELD_ERROR_KEYS) || {});
      setActionError(messageFromError(err));
      return false;
    } finally {
      if (busyOpRef.current === session.op) {
        busyOpRef.current = null;
        setBusy(false);
      }
    }
  };

  const handleNext = async () => {
    const originalN = COMPANY_STEPS[step - 1]?.originalN || 1;
    const ok = await saveThroughOriginalStep(originalN);
    if (ok && step < COMPANY_STEPS.length) setStep(step + 1);
  };

  const handleFinish = async ({ activate }) => {
    const ok = await saveThroughOriginalStep(6);
    const finishId = draftBindingMatches(draftBindingRef.current)
      ? draftBindingRef.current.id
      : propertyId;
    if (!ok || !finishId) return;
    if (activate) {
      const op = scopeGate.beginMutation({
        companyId,
        actorId,
        resourceId: finishId,
        membershipGeneration: membershipGenerationRef.current,
      });
      busyOpRef.current = op;
      setBusy(true);
      try {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await transport.activatePropertyForWork(finishId);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        toast.success("Корпоративний об'єкт активовано для роботи.");
      } catch (err) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          handleAuthorityLoss(status);
          return;
        }
        setActionError(messageFromError(err));
        return;
      } finally {
        if (busyOpRef.current === op) {
          busyOpRef.current = null;
          setBusy(false);
        }
      }
    } else {
      toast.success("Чернетку корпоративного об'єкта збережено.");
    }
    if (!mountedRef.current) return;
    navigate(`/companies/${companyId}/properties/${finishId}`);
  };

  const guardedMediaApi = useMemo(
    () =>
      createAuthorityBoundMediaApi({
        transport,
        scopeGate,
        getAuthority: () => authoritySnapshot(propertyId),
        mountedRef,
        onAuthorityLoss: handleAuthorityLoss,
      }),
    [authoritySnapshot, handleAuthorityLoss, propertyId, scopeGate, transport],
  );

  const guardedLandParcelsApi = useMemo(
    () =>
      createAuthorityBoundLandParcelsApi({
        transport,
        scopeGate,
        getAuthority: () => authoritySnapshot(propertyId),
        mountedRef,
        onAuthorityLoss: handleAuthorityLoss,
        fetchDetail: () =>
          propertyId ? transport.fetchPropertyDetail(propertyId) : null,
        onRefresh: async (detail) => {
          if (!mountedRef.current || !detail) return;
          applyPropertyDetail(detail);
        },
      }),
    [authoritySnapshot, handleAuthorityLoss, propertyId, scopeGate, transport],
  );

  const activeOriginal = COMPANY_STEPS[step - 1]?.originalN || 1;

  return (
    <div
      className="clients-page feature-company-workspace feature-company-properties add-property-page"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <CompanyPageHeader
          title="Новий об'єкт компанії"
          subtitle="Корпоративне створення · Компанія"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="properties"
          actions={
            <Link
              to={`/companies/${companyId}/properties`}
              className="action-btn action-btn--ghost"
            >
              До списку
            </Link>
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <EmptyState
            title="Корпоративні об'єкти недоступні"
            description="Створення об'єктів компанії вимкнене."
          />
        ) : null}
        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Створювати об'єкти компанії можуть лише активні учасники компанії."
          />
        ) : null}
        {state === "not-found" ? (
          <EmptyState title="Не знайдено" description="Компанію не знайдено." />
        ) : null}
        {state === "error" ? (
          <EmptyState
            title="Помилка"
            description={error || "Спробуйте ще раз."}
            primaryAction={
              <button
                type="button"
                className="action-btn"
                onClick={loadAuthority}
              >
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && available && canCreate ? (
          <div className="add-property-page__body">
            {actionError ? (
              <p className="feature-company-workspace__error" role="alert">
                {actionError}
              </p>
            ) : null}
            <section
              className="feature-company-workspace__card"
              aria-label="Відповідальний"
            >
              <h2>Відповідальний</h2>
              {propertyId ? (
                <p className="feature-company-workspace__meta">
                  Відповідального зафіксовано при створенні чернетки.
                </p>
              ) : canManage ? (
                <>
                  <div className="clients-toolbar__row" role="radiogroup" aria-label="Режим відповідального">
                    <label className="ui-form__check">
                      <input
                        type="radio"
                        name="responsibility-mode"
                        checked={responsibilityMode === "self"}
                        onChange={() => {
                          setResponsibilityMode("self");
                          setResponsibilityUserId("");
                        }}
                        disabled={busy || formLocked || writesSuspended}
                      />
                      Я відповідальний
                    </label>
                    <label className="ui-form__check">
                      <input
                        type="radio"
                        name="responsibility-mode"
                        checked={responsibilityMode === "other"}
                        onChange={() => setResponsibilityMode("other")}
                        disabled={busy || formLocked || writesSuspended}
                      />
                      Інший учасник
                    </label>
                    <label className="ui-form__check">
                      <input
                        type="radio"
                        name="responsibility-mode"
                        checked={responsibilityMode === "unassigned"}
                        onChange={() => {
                          setResponsibilityMode("unassigned");
                          setResponsibilityUserId("");
                        }}
                        disabled={busy || formLocked || writesSuspended}
                      />
                      Без відповідального
                    </label>
                  </div>
                  {responsibilityMode === "other" ? (
                    <Field id="responsibility-user" label="Учасник" required>
                      <Select
                        id="responsibility-user"
                        value={responsibilityUserId}
                        onChange={(e) =>
                          setResponsibilityUserId(e.target.value)
                        }
                        disabled={busy || formLocked || writesSuspended}
                        options={[
                          { value: "", label: "Оберіть учасника" },
                          ...rosterMembers.map((m) => ({
                            value: String(m.user?.id ?? m.user_id ?? ""),
                            label: memberDisplayName(m),
                          })),
                        ]}
                      />
                    </Field>
                  ) : null}
                </>
              ) : (
                <p>
                  Після створення ви будете відповідальним за цей об'єкт.
                </p>
              )}
            </section>
            <CreateStepper steps={COMPANY_STEPS} current={step} />
            <CreateStepType
              active={activeOriginal === 1}
              values={form.core}
              onChange={(core) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.core, core),
                  );
                  return { ...prev, core };
                });
              }}
              fieldErrors={fieldErrors}
              typeLocked={Boolean(propertyId)}
            />
            <CreateStepLocation
              active={activeOriginal === 2}
              values={form.location}
              onChange={(location) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.location, location),
                  );
                  return { ...prev, location };
                });
              }}
              fieldErrors={fieldErrors}
            />
            <CreateStepSpecs
              active={activeOriginal === 3}
              propertyType={form.core.property_type}
              residential={form.residential}
              land={form.land}
              commercial={form.commercial}
              fieldErrors={fieldErrors}
              onChangeResidential={(residential) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.residential, residential),
                  );
                  return { ...prev, residential };
                });
              }}
              onChangeLand={(land) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.land, land),
                  );
                  return { ...prev, land };
                });
              }}
              onChangeCommercial={(commercial) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.commercial, commercial),
                  );
                  return { ...prev, commercial };
                });
              }}
            />
            <div hidden={activeOriginal !== 3}>
              {propertyId ? (
                <LandParcelsEditor
                  key={`parcels-${childSessionKey}-${propertyId}`}
                  propertyId={propertyId}
                  propertyType={form.core.property_type}
                  initialParcels={landParcels}
                  onParcelsChange={setLandParcels}
                  api={guardedLandParcelsApi}
                  disabled={busy || formLocked || writesSuspended}
                />
              ) : null}
            </div>
            <section
              className={
                activeOriginal === 5 ? "ap-pane is-active" : "ap-pane"
              }
              hidden={activeOriginal !== 5}
              aria-label="Медіа"
            >
              {propertyId && property ? (
                <EditSectionMedia
                  key={`media-${childSessionKey}-${propertyId}`}
                  property={property}
                  mediaApi={guardedMediaApi}
                  videoUrl={form.core.video_url}
                  onVideoUrlChange={(video_url) => {
                    markFormDirty();
                    setForm((prev) => ({
                      ...prev,
                      core: { ...prev.core, video_url },
                    }));
                  }}
                  onMediaChanged={() => refreshProperty(propertyId)}
                />
              ) : (
                <p>Спочатку збережіть базові дані об'єкта.</p>
              )}
            </section>
            <CreateStepDescription
              active={activeOriginal === 6}
              values={form.core}
              onChange={(core) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.core, core),
                  );
                  return { ...prev, core };
                });
              }}
              fieldErrors={fieldErrors}
              submitting={busy}
              submitError={actionError}
              finishMode="corporate"
              onSaveDraft={() => handleFinish({ activate: false })}
              onPublish={() => handleFinish({ activate: true })}
            />
            {step < COMPANY_STEPS.length ? (
              <CreateNav
                current={step}
                total={COMPANY_STEPS.length}
                onPrev={() => setStep((s) => Math.max(1, s - 1))}
                onNext={handleNext}
                nextDisabled={busy || formLocked || writesSuspended}
              />
            ) : (
              <div className="clients-toolbar__row">
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy || formLocked || writesSuspended}
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                >
                  Назад
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CompanyPropertyCreateContent;
